import { SegmentationRule } from '@salecycle/experimental/Modal/SegmentationModal';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TargetAudience,
  TargetAudienceType,
} from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/CampaignEditorState';
import { useCampaignEditorContext } from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/hooks/useCampaignEditorContext';
import { FilterOptionsFactory } from 'sections/offsite-campaigns/offsite-campaign-editor/components/editor/segment-item/segment-filter-items/FilterOptionsFactory';

/**
 * Gets the rules that are usable for the chosen target audience.
 *
 * Users may switch target audience at any point and not all rules are applicable
 * to each target audience. This will filter out the rules that are not applicable.
 *
 * @returns The filtered rules list.
 */
export const useGetRulesApplicableForTargetAudience = (
  targetAudience: TargetAudience,
): SegmentationRule<any, any, any>[][] => {
  const { t } = useTranslation();
  const { state } = useCampaignEditorContext();

  const rules = useMemo(() => {
    const transferableOptions = {
      'browsedProducts.*.product.name': 'abandonedBasket.items.*.product.name',
      'abandonedBasket.items.*.product.name': 'browsedProducts.*.product.name',
      'browsedProducts.*.product.category.id': 'abandonedBasket.items.*.product.category.id',
      'abandonedBasket.items.*.product.category.id': 'browsedProducts.*.product.category.id',
      'browsedProducts.*.product.url': 'abandonedBasket.items.*.product.url',
      'abandonedBasket.items.*.product.url': 'browsedProducts.*.product.url',
      'browsedProducts.*.product.inStock': 'abandonedBasket.items.*.product.inStock',
      'abandonedBasket.items.*.product.inStock': 'browsedProducts.*.product.inStock',
      'browsedProducts.*.product.id': 'abandonedBasket.items.*.product.id',
      'abandonedBasket.items.*.product.id': 'browsedProducts.*.product.id',
    };

    // We only have one target audience at the moment so it's safe to grab the first one.
    if (!targetAudience) {
      return [[]];
    }

    const isBrowseAbandonment = targetAudience.audience === TargetAudienceType.BrowseAbandonment;
    const isPurchase = targetAudience.audience === TargetAudienceType.Purchase;
    const filterFactory = new FilterOptionsFactory(t, isBrowseAbandonment, true, isPurchase);

    const allowedRulesForThisTargetAudience = filterFactory.getOptionsList();

    const targetAudienceAdditionalSegmentation = state.campaign.additionalSegmentation[targetAudience.audience];
    if (!targetAudienceAdditionalSegmentation) {
      return [[]];
    }

    const additionalSegmentation = targetAudienceAdditionalSegmentation
      .map(AND => {
        return AND.map(rule => {
          /*
            We need to check for the following three things:
            - If the rule is found then the selected type already viable for the target audience and we can return the rule directly;
            - If the rule is transferable to another rule type then we re-create the rule but with an updated type;
            - If the rule isn't found and cannot be transferred then we need to leave it out of the list, so return null and filter out later.
          */
          const type = rule?.type;
          const found = allowedRulesForThisTargetAudience[type];
          if (found) {
            return rule;
          }

          const hasTransferableOption = allowedRulesForThisTargetAudience[transferableOptions[type]];
          if (hasTransferableOption) {
            return {
              ...rule,
              type: transferableOptions[type],
            };
          }

          return null;
        });
      })
      .filter(s => s[0]);

    return additionalSegmentation;
  }, [state.campaign.additionalSegmentation, t, targetAudience]);

  return rules.length ? rules : [[]];
};
